import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';
import { ApiUrlBuilder } from '../../config/api.config';
import { InviteStatus, PaginatedInvitesResponse, UserInvite } from '../../models/user.model';
import {
  AcceptInviteResponse,
  InviteApiItem,
  InviteListApiResponse,
  InviteOrganization,
  InviteOrganizationApiItem,
  InviteSendResult,
  InviteSendResultApiItem,
  ValidateInviteTokenApiResponse,
  ValidateInviteTokenResult,
} from '../../models/invite.model';

/**
 * Serviço responsável pelas chamadas HTTP relacionadas ao gerenciamento de convites.
 * Encapsula todas as requisições à API de convites sem gerenciar estado local.
 * Não injeta AuthService para evitar dependências circulares, mas inclui headers de autenticação quando necessário.
 */
@Injectable({
  providedIn: 'root',
})
export class InviteApiService {
  readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);

  /**
   * Retorna convites paginados da API.
   *
   * @param pageNumber Número da página (inicia em 1)
   * @param pageSize Quantidade de itens por página
   * @returns Resultado paginado compatível com o contrato da UI
   */
  async getUserInvites(pageNumber = 1, pageSize = 10): Promise<PaginatedInvitesResponse> {
    const safePage = Math.max(1, pageNumber);
    const safeSize = Math.max(1, pageSize);

    const url = `${ApiUrlBuilder.invites()}?pageNumber=${safePage}&pageSize=${safeSize}`;

    try {
      const response = await firstValueFrom(
        this.http.get<InviteListApiResponse>(url).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 204) {
              return of({
                items: [],
                totalCount: 0,
                pageNumber: safePage,
                pageSize: safeSize,
              } as InviteListApiResponse);
            }

            return throwError(() => error);
          }),
        ),
      );

      const list = response?.items ?? [];
      const totalCount = response?.totalCount ?? list.length;
      const pageNumberFromApi = response?.pageNumber ?? safePage;
      const pageSizeFromApi = response?.pageSize ?? safeSize;

      return {
        items: list.map((invite) => this.toUserInvite(invite)),
        totalCount,
        pageNumber: pageNumberFromApi,
        pageSize: pageSizeFromApi,
      };
    } catch (error) {
      console.error('Error fetching invites:', error);
      throw error;
    }
  }

  /**
   * Envia convites para uma lista de e-mails.
   *
   * @param emails Lista de e-mails que receberão convite
   * @param clientId Identificador da organização (opcional para perfil Super)
   * @returns Resultado individual de envio por e-mail
   */
  async sendInvites(emails: string[], clientId?: string): Promise<InviteSendResult[]> {
    const normalizedEmails = emails.map((email) => email.trim().toLowerCase()).filter((email) => !!email);

    if (normalizedEmails.length === 0) {
      return [];
    }

    const url = ApiUrlBuilder.invites();
    const requestBody = {
      emails: normalizedEmails,
      ...(clientId ? { clientId } : {}),
    };

    const response = await firstValueFrom(
      this.http.post<InviteSendResultApiItem[]>(url, requestBody).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error sending invites:', error);
          throw error;
        }),
      ),
    );

    return (response ?? []).map((item) => ({
      email: item.email ?? '',
      success: item.success ?? false,
      error: item.error,
      warning: item.warning,
    }));
  }

  /**
   * Verifica, antes do envio, se cada e-mail apresentaria bloqueio ou aviso ao ser
   * convidado — sem criar nem reenviar nenhum convite.
   *
   * @param emails Lista de e-mails a verificar
   * @param clientId Identificador da organização (opcional para perfil Super)
   * @returns Resultado individual de verificação por e-mail
   */
  async checkInviteWarnings(emails: string[], clientId?: string): Promise<InviteSendResult[]> {
    const normalizedEmails = emails.map((email) => email.trim().toLowerCase()).filter((email) => !!email);

    if (normalizedEmails.length === 0) {
      return [];
    }

    const url = ApiUrlBuilder.invites('check');
    const requestBody = {
      emails: normalizedEmails,
      ...(clientId ? { clientId } : {}),
    };

    const response = await firstValueFrom(
      this.http.post<InviteSendResultApiItem[]>(url, requestBody).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error checking invite warnings:', error);
          throw error;
        }),
      ),
    );

    return (response ?? []).map((item) => ({
      email: item.email ?? '',
      success: item.success ?? false,
      error: item.error,
      warning: item.warning,
    }));
  }

  /**
   * Valida um token de convite e retorna os dados necessários para aceite.
   *
   * @param token Token recebido no link de convite
   * @returns Dados do convite válido ou null quando inválido/expirado
   */
  async validateInviteToken(token: string): Promise<ValidateInviteTokenResult | null> {
    const normalizedToken = token.trim();
    if (!normalizedToken) {
      return null;
    }

    const encodedToken = encodeURIComponent(normalizedToken);
    const url = `${ApiUrlBuilder.invites('validate')}?token=${encodedToken}`;

    try {
      const response = await firstValueFrom(
        this.http.get<ValidateInviteTokenApiResponse>(url).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401 || error.status === 404 || error.status === 400) {
              return of(null);
            }

            return throwError(() => error);
          }),
        ),
      );

      if (!response) {
        return null;
      }

      const organizationId = response.organizationId ?? '';
      const email = response.email ?? '';
      const isNewUser = response.isNewUser ?? false;
      const expiresAt = this.toDate(response.expiresAt);

      if (!organizationId || !email) {
        return null;
      }

      return {
        organizationId,
        email,
        isNewUser,
        expiresAt,
        invitingOrganization: this.toInviteOrganization(response.invitingOrganization),
        currentOrganization: this.toInviteOrganization(response.currentOrganization),
      };
    } catch (error) {
      console.error('Error validating invite token:', error);
      throw error;
    }
  }

  /**
   * Aceita um convite vinculado ao usuário autenticado.
   *
   * @param token Token recebido no link de convite
   * @returns Resposta da API com mensagem de confirmação
   */
  async acceptInvite(token: string): Promise<AcceptInviteResponse> {
    const normalizedToken = token.trim();
    if (!normalizedToken) {
      throw new Error(this.transloco.translate('shared.apiErrors.inviteTokenRequired'));
    }

    const encodedToken = encodeURIComponent(normalizedToken);
    const url = `${ApiUrlBuilder.invites('accept')}?token=${encodedToken}`;

    return firstValueFrom(
      this.http.post<AcceptInviteResponse>(url, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error accepting invite:', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Aceita convite para usuario existente sem autenticacao.
   *
   * @param token Token recebido no link de convite
   * @param email E-mail do usuario existente
   */
  async acceptInvitePublic(token: string, email: string): Promise<AcceptInviteResponse> {
    const normalizedToken = token.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedToken) {
      throw new Error(this.transloco.translate('shared.apiErrors.inviteTokenRequired'));
    }

    if (!normalizedEmail) {
      throw new Error(this.transloco.translate('shared.apiErrors.emailRequired'));
    }

    const encodedToken = encodeURIComponent(normalizedToken);
    const encodedEmail = encodeURIComponent(normalizedEmail);
    const url = `${ApiUrlBuilder.invites('acceptPublic')}?token=${encodedToken}&email=${encodedEmail}`;

    return firstValueFrom(
      this.http.post<AcceptInviteResponse>(url, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error accepting invite publicly:', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Reenvia um convite pendente ou expirado via mesmo endpoint de geração.
   *
   * @param email E-mail do usuário que receberá o novo convite
   * @returns Promise resolvida quando o reenvio terminar
   */
  async resendInvite(email: string): Promise<void> {
    const results = await this.sendInvites([email]);
    const result = results[0];

    if (!result?.success) {
      throw new Error(result?.error ?? this.transloco.translate('shared.apiErrors.resendInvite'));
    }
  }

  /**
   * Exclui um convite pelo identificador.
   *
   * @param inviteId Identificador unico do convite
   */
  async deleteInvite(inviteId: string): Promise<void> {
    const normalizedInviteId = inviteId.trim();
    if (!normalizedInviteId) {
      throw new Error(this.transloco.translate('shared.apiErrors.inviteIdRequired'));
    }

    // Evita enviar token/email como id para o endpoint de exclusao.
    if (normalizedInviteId.includes('@') || normalizedInviteId.length > 64) {
      throw new Error(this.transloco.translate('shared.apiErrors.invalidInviteDeleteId'));
    }

    const encodedInviteId = encodeURIComponent(normalizedInviteId);
    const url = ApiUrlBuilder.invites('base', encodedInviteId);

    await firstValueFrom(
      this.http.delete<void>(url).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error deleting invite:', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Converte o item bruto retornado pela API para o modelo usado na UI.
   *
   * @param invite Item de convite recebido da API
   * @returns Convite normalizado para uso na aplicação
   */
  private toUserInvite(invite: InviteApiItem): UserInvite {
    const inviteId = invite.id ?? invite.inviteId ?? invite.invitationId;

    const token = invite.token ?? '';
    const email = invite.email ?? '';
    const clientId = invite.clientId ?? '';

    return {
      id: (inviteId ?? token) || email,
      inviteId,
      token,
      email,
      clientId,
      status: this.parseStatus(invite.status),
      expiresAt: this.toDate(invite.expiresAt),
      isNewUser: invite.isNewUser ?? false,
      createdAt: this.toDate(invite.createdAt),
      updatedAt: this.toDate(invite.updatedAt),
      isActive: invite.isActive ?? true,
      xmin: invite.xmin ?? 0,
      nome: invite.nome,
      createdBy: invite.createdBy,
      acceptedAt: this.toOptionalDate(invite.acceptedAt),
      previousOrganizationName: invite.previousOrganizationName,
    };
  }

  /**
   * Normaliza o status do convite para o enum local da aplicação.
   *
   * @param status Status em formato numérico ou textual vindo da API
   * @returns Status do convite no formato InviteStatus
   */
  private parseStatus(status: unknown): InviteStatus {
    if (typeof status === 'number') {
      switch (status) {
        case 1:
          return InviteStatus.Accepted;
        case 2:
          return InviteStatus.Expired;
        case 0:
        default:
          return InviteStatus.Pending;
      }
    }

    const normalizedStatus = (typeof status === 'string' ? status : '').toLowerCase();

    if (normalizedStatus.includes('accepted') || normalizedStatus.includes('aceito')) {
      return InviteStatus.Accepted;
    }

    if (normalizedStatus.includes('expired') || normalizedStatus.includes('expirado')) {
      return InviteStatus.Expired;
    }

    return InviteStatus.Pending;
  }

  /**
   * Converte um valor de data obrigatório para Date.
   *
   * @param value Valor de data em string, Date ou indefinido
   * @returns Data convertida; quando indefinido retorna a data atual
   */
  /**
   * Converte a organização retornada pela API para o modelo usado na tela de transição.
   *
   * @param value Organização vinda da API, possivelmente ausente
   * @returns Organização normalizada ou null quando não houver dados utilizáveis
   */
  private toInviteOrganization(value: InviteOrganizationApiItem | null | undefined): InviteOrganization | null {
    const id = value?.id?.trim();
    const name = value?.name?.trim();
    const logoUrl = value?.logoUrl?.trim();

    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      logoUrl: logoUrl?.length ? logoUrl : null,
      isActive: value?.isActive ?? true,
    };
  }

  private toDate(value: string | Date | undefined): Date {
    if (!value) {
      return new Date();
    }

    return value instanceof Date ? value : new Date(value);
  }

  /**
   * Converte um valor de data opcional para Date.
   *
   * @param value Valor de data em string, Date ou indefinido
   * @returns Data convertida ou undefined quando não informado
   */
  private toOptionalDate(value: string | Date | undefined): Date | undefined {
    if (!value) {
      return undefined;
    }

    return value instanceof Date ? value : new Date(value);
  }
}
